import React, { useState, useCallback, useEffect } from 'react'
import { CheckBoxCardSelectInput } from '../Select/CheckBoxCardSelectInput'
import {
  ContentType,
  LessonData,
  TrialLessonIndexView,
} from '../../types/trialLessons'
import { CurrentUser } from '../../types/currentUser'
import { fetchLessons } from '../../api/request'

import sheGirlRed from '../../public/she_girl_red.png'
import sheGirlBlue from '../../public/she_girl_blue.png'
import RecommendLabelRed from '../../public/recommend_label_red.png'
import RecommendLabelBlue from '../../public/recommend_label_blue.png'

import useSWRMutation from 'swr/mutation'

import chevronDownImage from '../../public/chevron-down.png' // Import chevron-down image

import {
  ReservationStepBody,
  ReservationStepSubHeader,
} from '../ReservationStepHeader'

import { TrialLessonCalendar } from '../TrialLessonCalendar'
import { SelectedLessonTypeSummary2 } from '../SelectedLessonTypeSummary2'
import { FancyDate } from '../FancyDate'
import { UserFormPane } from '../UserFormPane'
import { SelectedLessonSummary2 } from '../SelectedLessonSummary2'

import { getLessonPeriod } from '../../utils/dateLessonPeriod'
import { LocationKey, LocationsKey } from '../../types/location'

export type LessonTypeId = 'class' | 'consultation'

export type LessonTypes = {
  id: 'class' | 'consultation'
  value: 'class' | 'consultation'
  recommendLabel: string
  sheGirl: string
  headerText: string
  label: '人気コース体験' | '少人数相談会'
  description: string
  time: string
}

export const LESSON_TYPES: LessonTypes[] = [
  {
    id: 'class',
    value: 'class',
    recommendLabel: RecommendLabelRed.src,
    sheGirl: sheGirlRed.src,
    headerText: 'SHElikesについて知りたい人におすすめ。',
    label: '人気コース体験',
    description:
      'SHElikesのサービスと学んだスキルの活用法をプチお仕事体験を通してご紹介します。\n後半のカウンセリングでは受講相談も可能です。',
    time: '90',
  },
  {
    id: 'consultation',
    value: 'consultation',
    recommendLabel: RecommendLabelBlue.src,
    sheGirl: sheGirlBlue.src,
    headerText:
      '過去に体験レッスンに参加したことがある人や少人数で具体的な相談をしたい人におすすめ。',
    label: '少人数相談会',
    description:
      'あなたに合ったコースのプランニングやご相談をお受けし、ご入会までサポートします。\n※過去に体験レッスンに参加したことがある方はこちらを選択ください。',
    time: '60',
  },
]

export function getLessonTypeSpec(lessonTypeId: LessonTypeId) {
  return LESSON_TYPES.find(t => t.id === lessonTypeId)
}

type RescheduleType = 'class' | 'consultation'
export const LessonListContent = ({
  jwtToken,
  currentUser,
  contentType,
  isRescheduled,
  rescheduleType,
  currentReservedLesson,
  reservationId,
  location,
  isShares,
}: {
  jwtToken: string
  currentUser?: CurrentUser
  contentType: ContentType
  isRescheduled?: boolean
  rescheduleType?: RescheduleType
  currentReservedLesson?: TrialLessonIndexView
  reservationId?: string
  location?: LocationsKey
  isShares?: boolean
}) => {
  const [selectedLesson, setSelectedLesson] =
    useState<TrialLessonIndexView | null>(null)
  const [isAffiliate, setIsAffiliate] = useState<boolean | null>(null)
  const isLocation = location && Object.values(LocationKey).includes(location)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const campaigns = params.getAll('utm_campaign')
    setIsAffiliate(
      campaigns.some(
        campaign =>
          campaign.includes('A8_incentive_pointincome') ||
          campaign.includes('ace_incentive_pointincome')
      )
    )
  }, [])

  useEffect(() => {
    if (isRescheduled) {
      onChangeSelectedLessonTypeId(rescheduleType as RescheduleType)
    }
  }, [rescheduleType, isAffiliate])

  const [selectedLessonTypeId, setSelectedLessonTypeId] =
    useState<LessonTypeId | null>(null)

  const [lessonData, setLessonData] = useState<LessonData | null>(null)

  useEffect(() => {
    if (isAffiliate === null) return
    if (contentType !== 'normal') {
      onChangeSelectedLessonTypeId(
        contentType === 'onlyOtl' ? 'class' : 'consultation'
      )
      return
    }
  }, [isAffiliate])

  const { trigger } = useSWRMutation(
    `${process.env.NEXT_PUBLIC_API_URL}/api/v3/trial_lessons/data_for_calendar_display`,
    fetchLessons
  )

  const onChangeSelectedLessonTypeId = useCallback(
    async (id: LessonTypeId | null) => {
      setSelectedLessonTypeId(id)

      if (!id) {
        setSelectedLesson(null)
        return
      }
      setLessonData(null)
      if (isAffiliate === null) return
      try {
        let location_key: string | undefined
        let lesson_type_name: string | undefined

        const period = getLessonPeriod({ boundaryTime: 20 })
        const start_date = period.start_date
        const end_date = period.end_date

        if (isLocation) {
          location_key = location
        } else {
          lesson_type_name =
            id === 'class' ? 'webデザイン/マーケ・ライター' : '少人数相談会'
        }

        const result = await trigger({
          lesson_type_name,
          start_date,
          end_date,
          is_affiliate: isAffiliate,
          location_key,
        })

        let lessons = result.body as LessonData

        // 拠点の場合は予約可能な日付のみを取得
        if (isLocation && lessons) {
          const original = { ...lessons }
          const filtered = Object.fromEntries(
            Object.entries(original).filter(([_, dayData]) =>
              Object.values(dayData.slots).some(
                slot => slot.status === 'reservable' || slot.status === 'full'
              )
            )
          )

          // フィルタリング結果が空のオブジェクトになった場合、元のリストを使用する
          if (Object.keys(filtered).length === 0) {
            lessons = original // 元のレッスンデータをセット
          } else {
            lessons = filtered // フィルタリングされたレッスンデータをセット
          }
        }

        setLessonData(lessons)
      } catch (e) {
        // error handling if necessary
      }
    },
    [selectedLesson, isAffiliate, isLocation, location]
  )

  const isOnly = contentType.includes('only')

  return (
    <>
      <div className="mx-auto max-w-[1000px]">
        <h2 className="text-[20px] font-bold tracking-[0.18em] ">
          {!isLocation && !isShares && (
            <>
              ご希望の参加日時を
              <br className="hidden sp:block" />
              お選びください。
            </>
          )}
        </h2>
        {isLocation && (
          <img
            src={chevronDownImage.src}
            alt="Chevron down"
            className="w-[120px] h-[30px] sp:w-[57px] sp:h-[30px] m-auto"
          />
        )}
        <ReservationStepBody>
          {selectedLesson?.id ? (
            <div
              className={`desktop:max-w-[85%] mx-auto desktop:py-12  sp:py-[40px] ${
                isShares && 'desktop:pt-6 desktop:pb-0'
              }`}
              id="selector-wrapper"
            >
              <div className="tracking-[2px] desktop:flex items-center justify-center sp:mx-4">
                <FancyDate date={selectedLesson.starts_at} />
                <div className="desktop:ml-9 sp:mt-5">
                  {!isOnly && !isRescheduled && (
                    <SelectedLessonTypeSummary2
                      selectedLessonTypeId={
                        selectedLessonTypeId as LessonTypeId
                      }
                      onUnselect={() => onChangeSelectedLessonTypeId(null)}
                    />
                  )}
                  <div className="mt-3">
                    <SelectedLessonSummary2
                      lesson={selectedLesson}
                      onUnselect={() => {
                        setSelectedLesson(null)
                        window.scroll({
                          top: (
                            window.document.querySelector(
                              '#selector-wrapper'
                            ) as HTMLElement
                          ).offsetTop,
                          behavior: 'smooth',
                        })
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {!isRescheduled && !isLocation && (
                <div
                  className={`desktop:max-w-[85%] mx-auto desktop:my-12 sp:my-[40px] ${
                    isOnly ? 'hidden' : ''
                  }`}
                  id="trial-content-selector"
                >
                  <CheckBoxCardSelectInput
                    options={LESSON_TYPES.map(t => ({
                      label: t.label,
                      value: t.value,
                      recommendLabel: t.recommendLabel,
                      sheGirl: t.sheGirl,
                      headerText: t.headerText,
                      htmlDataValue: `area-${t.id}`,
                      description: t.description,
                    }))}
                    value={selectedLessonTypeId}
                    onChange={onChangeSelectedLessonTypeId}
                    widthType="m"
                    arrangementType="horizon"
                    gaSendEventCategory="trial"
                    gaSendEventEventName="begin_checkout_step2"
                    addGaSendEventEventNameDetail={true}
                    contentType={contentType}
                  />
                </div>
              )}
              {selectedLessonTypeId && (
                <>
                  <div className="desktop:max-w-[85%] mx-auto desktop:my-12 sp:my-[40px]">
                    {!isShares ? (
                      <>
                        {isLocation ? (
                          <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen bg-primary text-white font-bold rounded-[2px] py-[18px] px-6 text-[18px] mt-6 desktop:mb-4 desktop:py-[14px]">
                            お申し込み
                          </div>
                        ) : (
                          <div className="bg-bg-gray-0 text-center text-primary font-bold rounded-[2px] py-4 px-6 text-[14px] mx-auto max-w-[600px] desktop:mb-4">
                            「{getLessonTypeSpec(selectedLessonTypeId)?.label}
                            」所要時間：
                            {getLessonTypeSpec(selectedLessonTypeId)?.time}分
                          </div>
                        )}
                      </>
                    ) : null}

                    <ReservationStepSubHeader>
                      ご希望の開始日時をカレンダーから選択してください
                      {
                        /* JST と異なるタイムゾーンからのアクセスの方のみ表示しておく */
                        new Date().getTimezoneOffset() !== -540 && (
                          <>
                            <br /> カレンダーの日時は日本時間となります
                          </>
                        )
                      }
                    </ReservationStepSubHeader>
                    <TrialLessonCalendar
                      setSelectedLesson={setSelectedLesson}
                      lessonData={lessonData || undefined}
                      currentReservedLesson={currentReservedLesson}
                      isRescheduled
                      contentType={contentType}
                    />
                  </div>
                </>
              )}
            </>
          )}
        </ReservationStepBody>
      </div>
      {selectedLesson && (
        <div className="mx-auto max-w-[1000px]">
          <UserFormPane
            stepText="STEP2"
            id="step2"
            jwtToken={jwtToken}
            selectedLesson={selectedLesson}
            currentUser={currentUser}
            isRescheduled={isRescheduled}
            reservationId={reservationId}
            location={location}
            isShares={isShares}
            contentType={contentType}
          />
        </div>
      )}
    </>
  )
}
